import React, { useCallback, useState } from "react";
import { Image, Title, Text, Input, Button, ButtonLink, Field, Toast } from "components/commons";
import { SoCashlessLogo1 } from "images";
import { useForm, useApi, useRouter } from "hooks";
import { resetPassword } from "apis";
import { Field as FieldType, StyleType } from "enums";
import Validation from "services/validation.service";
import { Path } from "paths";
import lang from "translations";

const passwordValidations = [
  Validation.required(),
  Validation.minlength(8),
  Validation.maxlength(16),
];

const initialState = {
  password: {
    name: "password",
    value: "",
    type: FieldType.INPUT,
    validations: passwordValidations,
    required: true,
    minLength: 8,
    maxLength: 16,
    label: lang.newPassWord,
    placeholder: lang.choosePassword,
  },
  confirmPassword: {
    name: "confirmPassword",
    value: "",
    type: FieldType.INPUT,
    validations: passwordValidations,
    required: true,
    minLength: 8,
    maxLength: 16,
    label: lang.confirmPassword,
    placeholder: lang.confirmPassword,
  },
};

const ResetPassword = () => {
  const { history, query } = useRouter();
  const { token } = query || {};
  const [hidePassword, setHidePassword] = useState(true);
  const [errorMessage, setErrorMessage] = useState();
  const { fields, modifyField, applyFieldErrors, submitForm } = useForm({ initialState });
  const { password, confirmPassword } = fields;

  const { request, loading } = useApi({
    api: resetPassword,
    handleOwnError: true,
  });

  const handleSubmit = useCallback(async () => {
    if (password.value !== confirmPassword.value) {
      applyFieldErrors({ confirmPassword: lang.passwordsDoNotMatch });
      return;
    }
    setErrorMessage(null);
    try {
      await request({ token, password: password.value });
      Toast({
        content: lang.passwordResetSuccessfully,
        success: true,
        icon: "check",
      }).open();
      history.push(Path.SLASH);
    } catch (e) {
      setErrorMessage(e?.metadata?.message || lang.resetPasswordFailed);
    }
  }, [password.value, confirmPassword.value, applyFieldErrors, request, token, history]);

  const submitCb = useCallback(() => {
    if (loading) return;
    submitForm(handleSubmit);
  }, [loading, submitForm, handleSubmit]);

  const onPasswordChange = useCallback(
    (name, obj) => {
      setErrorMessage(null);
      modifyField(name, obj);
    },
    [modifyField],
  );

  if (!token) {
    return (
      <div className="mt-lg flex flex-col items-center text-center w-11/12 ml-sm sm:w-96">
        <Image className="mb-md" src={SoCashlessLogo1} />
        <Title className="mb-lg" xl>
          {lang.resetPassword}
        </Title>
        <Text error className="mb-md">
          {lang.invalidResetPasswordLink}
        </Text>
        <Button onClick={() => history.push(Path.FORGOT_PASSWORD)}>{lang.forgotPassword}</Button>
      </div>
    );
  }

  return (
    <div className="mt-lg flex flex-col items-center w-11/12 ml-sm sm:w-96">
      <Image className="mb-md" src={SoCashlessLogo1} />
      <Title className="mb-lg" xl>
        {lang.resetPassword}
      </Title>
      <Text className="mb-md text-center">{lang.enterNewPassword}</Text>
      <Field
        {...password}
        className="w-full mb-md"
        labelSuffix={
          <ButtonLink
            onClick={() => setHidePassword(!hidePassword)}
            prefix
            icon={hidePassword ? "eye-open" : "eye-close"}
          >
            {hidePassword ? lang.show : lang.hide}
          </ButtonLink>
        }
      >
        <Input
          {...password}
          inputType={hidePassword ? "password" : ""}
          onChange={onPasswordChange}
          onEnter={submitCb}
        />
      </Field>
      <Field {...confirmPassword} className="w-full mb-md">
        <Input
          {...confirmPassword}
          inputType={hidePassword ? "password" : ""}
          onChange={onPasswordChange}
          onEnter={submitCb}
        />
      </Field>
      {errorMessage && (
        <Text error className="mb-md text-center">
          {errorMessage}
        </Text>
      )}
      <Button onClick={submitCb} loading={loading} disabled={loading}>
        {lang.resetPassword}
      </Button>
      <Button
        className="mt-sm text-sm text-gray"
        type={StyleType.Link}
        onClick={() => history.push(Path.AUTH)}
      >
        {lang.backToLogin}
      </Button>
    </div>
  );
};

export default ResetPassword;
